import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { TONE_CLASS } from "../../../../components/BdrDisclosure/BdrDisclosure";

/**
 * ClientConsoleTiles — the eight navigation tiles across the top of the client
 * console. Four across, compacting rather than stacking as width drops.
 *
 * A tile is NAVIGATION, not a disclosure: it swaps what the VS below shows and
 * never opens in place. Hence the chevron points right, and the selected one
 * grows an arrow pointing DOWN at the panel it drives.
 *
 * COLOUR REPORTS STATUS, NEVER IDENTITY. Every icon tile is the same neutral
 * treatment. A section whose icon is permanently red has no way left to say
 * something is wrong with it.
 *
 * A TILE REPORTS A STATE, NOT A SIZE — see the rule at the top of
 * clientSectionSelectors.js. Only Notes and Photos carry a neutral count;
 * everything else shows toned numbers for what needs attention, and shows
 * nothing at all when nothing does.
 *
 * ─────────────────────────────────────────────────────────────────
 * HOW MANY BADGES FIT IS MEASURED, NOT GUESSED.
 *
 * Emails can report urgent + flagged + unread at once. A fixed rule either
 * truncates the label at narrow widths or wastes real space at wide ones — a
 * hard "one badge below 600px" left two thirds of the tile empty.
 *
 * So each tile measures its own row and shows as many statuses as genuinely
 * fit, most urgent first. The label's full width is reserved FIRST, so it is
 * never the thing that gives way.
 *
 * WHY THIS CANNOT LOOP, which is the usual hazard with measure-then-render:
 * a tile's width comes from its flex basis, not its content, so adding or
 * removing a badge cannot change the width that was just measured. The
 * observer therefore has no path back to itself. (Contrast `useNavHeightVar`,
 * where a measurement DID feed a CSS variable that resized the thing being
 * measured.) The rAF throttle and the only-write-on-change guard are belt and
 * braces on top of that.
 * ─────────────────────────────────────────────────────────────────
 *
 * The tone map is imported from BdrDisclosure rather than redeclared, so a
 * `{tone:"danger"}` payload paints the same whether it lands in a tile or a
 * disclosure row. A status is a FILLED BADGE, never coloured text — saturated
 * red or amber text on these surfaces triggers chromostereopsis and floats off
 * the panel (index.css, §7b).
 *
 * NOT A TABLIST. The first cut declared `role="tablist"`/`role="tab"` without
 * `aria-controls`, roving tabindex or arrow-key handling — half a tablist is
 * worse than none, because it promises a keyboard model that is not there.
 * These are buttons that mark the current section with `aria-current`.
 */

/* Used only until a real badge has been measured. */
const BADGE_W_FALLBACK = 26;
const GAP_FALLBACK = 8;

const ClientConsoleTiles = ({ tiles, activeKey, onSelect }) => {
  const containerRef = useRef(null);
  /** key -> { top, label } DOM nodes */
  const nodes = useRef({});
  /** key -> how many status badges fit */
  const [fits, setFits] = useState({});

  const measure = useCallback(() => {
    const next = {};

    tiles.forEach((tile) => {
      const statuses = tile.statuses || [];
      if (!statuses.length) return;

      const refs = nodes.current[tile.key];
      const top = refs?.top;
      const label = refs?.label;
      if (!top || !label) return;

      const style = window.getComputedStyle(top);
      const gap = parseFloat(style.columnGap || style.gap) || GAP_FALLBACK;

      /*
       * `scrollWidth`, not `offsetWidth`: the label carries `overflow: hidden`
       * and `text-overflow: ellipsis`, so its OFFSET width is whatever it has
       * been squeezed to, while its SCROLL width is the text's natural width.
       * Measuring the squeezed one would reserve the truncated size and
       * happily keep it truncated.
       */
      const labelNatural = label.scrollWidth;

      /* A real badge if one is on screen; the fallback only ever applies on
         the very first pass of a tile that has none rendered yet. */
      const badgeEl = top.querySelector(".bdr-badge");
      const badgeW = badgeEl?.offsetWidth || BADGE_W_FALLBACK;

      /* The neutral count is not optional — it is reserved before statuses. */
      const countEl = top.querySelector(".scc-tile-count");
      const reserved = countEl ? countEl.offsetWidth + gap : 0;

      const available = top.clientWidth - labelNatural - reserved;
      /* Each badge costs its own width PLUS the gap in front of it. */
      const room = Math.floor(available / (badgeW + gap));

      /*
       * At least one, always. A tile exists to say whether it needs you, and a
       * section with something urgent showing no badge at all is a worse
       * failure than a label clipped by a few pixels. In practice even the
       * 120px compact tile fits one.
       */
      next[tile.key] = Math.max(1, Math.min(statuses.length, room));
    });

    /* Only write when something actually changed — a re-render per observer
       tick is how a harmless measurement becomes a performance bug. */
    setFits((prev) => {
      const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);
      for (const k of keys) {
        if (prev[k] !== next[k]) return next;
      }
      return prev;
    });
  }, [tiles]);

  /* Before paint, so a tile never flashes three badges and then drops to one. */
  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;

    let raf = 0;
    const observer = new ResizeObserver(() => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        measure();
      });
    });
    observer.observe(el);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [measure]);

  return (
    <div className="scc-tiles" ref={containerRef}>
      {tiles.map((tile) => {
        const isActive = tile.key === activeKey;
        /*
         * The dim treatment is for a section that reports a count and that
         * count is zero — Notes and Photos. `undefined` means "reports no
         * count", which every other section now does deliberately, and a
         * section reporting nothing because nothing needs attention must NOT
         * read as empty.
         */
        const isEmpty = tile.count === 0;
        const statuses = tile.statuses || [];
        /* Default to ALL until measured; the layout effect trims before
           paint, so this is never what the user sees. */
        const shown = statuses.slice(0, fits[tile.key] ?? statuses.length);
        const hidden = statuses.length - shown.length;

        return (
          <button
            key={tile.key}
            type="button"
            aria-current={isActive ? "true" : undefined}
            className={[
              "scc-tile",
              isActive ? "is-active" : "",
              isEmpty ? "is-empty" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => onSelect(tile.key)}
          >
            <span className="scc-tile-icon">{tile.icon}</span>

            <span className="scc-tile-body">
              <span
                className="scc-tile-top"
                ref={(el) => {
                  nodes.current[tile.key] = {
                    ...(nodes.current[tile.key] || {}),
                    top: el,
                  };
                }}
              >
                <span
                  className="scc-tile-label"
                  ref={(el) => {
                    nodes.current[tile.key] = {
                      ...(nodes.current[tile.key] || {}),
                      label: el,
                    };
                  }}
                >
                  {tile.label}
                </span>

                {tile.count !== undefined && (
                  <span className="bdr-badge scc-tile-count">
                    {isEmpty ? "–" : tile.count}
                  </span>
                )}

                {shown.map((status, i) => (
                  <span
                    key={status.tone}
                    className={[
                      "bdr-badge",
                      "scc-tile-status",
                      TONE_CLASS[status.tone] || "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    /*
                     * When anything is hidden, the FIRST badge carries the
                     * whole set — otherwise what did not fit is gone for a
                     * screen reader as well as for the eye.
                     */
                    title={
                      i === 0 && hidden
                        ? statuses.map((st) => st.text).join(" · ")
                        : status.text
                    }
                    aria-label={
                      i === 0 && hidden
                        ? statuses.map((st) => st.text).join(", ")
                        : status.text
                    }
                  >
                    {status.value}
                  </span>
                ))}
              </span>
            </span>

            <svg
              className="scc-tile-chevron"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M9 6l6 6-6 6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        );
      })}
    </div>
  );
};

export default ClientConsoleTiles;
